
import Konva from '../../../konva';


import { HIDE_LOADER, SET_SAVED } from '../actions';
import {
  ELLIPSE_CROP,
  IMAGE_NODE_ID,
  SUPPORTED_IMAGE_TYPES,
  TOOLS_IDS,
} from '../utils/constants';
import extractCurrentDesignState from '../utils/extractCurrentDesignState';
import getFileFullName from '../utils/getFileFullName';
import getSizeAfterRotation from '../utils/getSizeAfterRotation';
import imageToBase64 from '../utils/imageToBase64';
import mapCropBox from '../utils/mapCropBox';
import operationsToCloudimageUrl from '../utils/operationsToCloudimageUrl';
import useStore from './useStore';

const EXPORT_EDGE_BLEED_PX = 2;
const EXPORT_BACKGROUND_FILL = '#ffffff';

const getExportPixelRatio = (pixelRatio, savingPixelRatio) => {
  const raw = Number(pixelRatio || savingPixelRatio);
  if (!Number.isFinite(raw) || raw <= 0) {
    return 1;
  }
  // Integer ratios avoid subpixel black edges; ceil so 1.2 does not collapse to 1×
  // (which made downloads/saves softer than intended).
  return Math.max(1, Math.ceil(raw - 1e-9));
};

const addExportBackgroundFill = (designLayer, clipBox, transparentBackground) => {
  if (transparentBackground || !designLayer || !clipBox?.width || !clipBox?.height) {
    return;
  }

  const bleed = EXPORT_EDGE_BLEED_PX;
  const bgRect = new Konva.Rect({
    x: Math.max(0, (clipBox.x || 0) - bleed),
    y: Math.max(0, (clipBox.y || 0) - bleed),
    width: clipBox.width + bleed * 2,
    height: clipBox.height + bleed * 2,
    fill: EXPORT_BACKGROUND_FILL,
    listening: false,
    perfectDrawEnabled: false,
  });

  designLayer.add(bgRect);
  bgRect.moveToBottom();
};

const useTransformedImgData = () => {
  const state = useStore();
  const {
    dispatch,
    designLayer,
    shownImageDimensions,
    originalImage,
    adjustments: { crop, rotation = 0, isFlippedX, isFlippedY },
    config: {
      savingPixelRatio,
      previewPixelRatio,
      forceToPngInEllipticalCrop,
      defaultSavedImageType,
      useCloudimage,
      cloudimage,
    },
  } = state;

  const getTransformedCloudimageData = (imageFileInfo = {}) => {
    const { filter, ...designState } = extractCurrentDesignState(state);
    const cloudimageUrl = operationsToCloudimageUrl(
      cloudimage,
      designState,
      shownImageDimensions,
      originalImage,
    );
    const mappedCropBox = mapCropBox(
      {
        x: crop.x,
        y: crop.y,
        width: crop.width,
        height: crop.height,
      },
      shownImageDimensions,
      originalImage,
    );

    const imageData = {
      cloudimageUrl,
      width: imageFileInfo?.size?.width || mappedCropBox.width,
      height: imageFileInfo?.size?.height || mappedCropBox.height,
    };

    return {
      imageData,
      designState,
    };
  };

  const getTransformedImgData = (
    imageFileInfo = {},
    pixelRatio = false,
    keepLoadingSpinnerShown = false,
    exportOptions = {},
  ) => {
    const exportPixelRatio = getExportPixelRatio(pixelRatio, savingPixelRatio);
    Konva.pixelRatio = exportPixelRatio;
    const { clipWidth, clipHeight, clipX, clipY } = designLayer.attrs;

    // We're using this for letting the designLayer's clipFunc know that we are in saving mode
    // so it should apply elliptical crop if it is not applied and user is chosing ellitpical ratio.
    designLayer.setAttr('isSaving', true);

    const preparedCanvas = designLayer.getStage().clone({
      width: originalImage.width,
      height: originalImage.height,
    });

    const [preparedDesignLayer] = preparedCanvas.children; // children[0] = Design layer
    preparedCanvas.children[1].destroy(); // children[1] = Transformers layer, which is not needed anymore
    const imgNode = preparedCanvas.findOne(`#${IMAGE_NODE_ID}`);
    const transparentBackground = Boolean(exportOptions.transparentBackground);
    if (transparentBackground && imgNode) {
      imgNode.hide();
    }
    if (imgNode) {
      imgNode.cache({ pixelRatio: exportPixelRatio });
    }

    const preparedDesignLayerScale = {
      x: preparedCanvas.width() / shownImageDimensions.width,
      y: preparedCanvas.height() / shownImageDimensions.height,
    };
    preparedDesignLayer.setAttrs({
      rotation: 0,
      offsetX: 0,
      offsetY: 0,
      x: 0,
      y: 0,
      scaleX: preparedDesignLayerScale.x,
      scaleY: preparedDesignLayerScale.y,
    });

    const exportClipBox = {
      x: crop.x ?? clipX ?? 0,
      y: crop.y ?? clipY ?? 0,
      width: crop.width || clipWidth || shownImageDimensions.width,
      height: crop.height || clipHeight || shownImageDimensions.height,
    };
    addExportBackgroundFill(
      preparedDesignLayer,
      exportClipBox,
      transparentBackground,
    );

    const requestedExtension = (
      exportOptions.extension ||
      imageFileInfo.extension ||
      defaultSavedImageType
    )
      ?.toLowerCase()
      ?.replace(/^\./, '');

    const resolvedExtension =
      requestedExtension === 'png' ||
      (forceToPngInEllipticalCrop && crop.ratio === ELLIPSE_CROP)
        ? 'png'
        : requestedExtension &&
            SUPPORTED_IMAGE_TYPES.includes(requestedExtension)
          ? requestedExtension
          : 'jpeg';

    const {
      name,
      extension = resolvedExtension,
      quality = resolvedExtension === 'png' ? undefined : 0.92,
      size = {},
    } = {
      ...((!imageFileInfo.name || !imageFileInfo.extension) &&
        getFileFullName(originalImage.name, resolvedExtension)),
      ...imageFileInfo,
      extension: resolvedExtension,
    };

    const normalizedExtension = extension === 'jpg' ? 'jpeg' : extension;
    const isQualityAcceptable = ['jpeg', 'webp'].includes(normalizedExtension);
    const mimeType =
      normalizedExtension === 'png' ? 'image/png' : `image/${normalizedExtension}`;

    const mappedCropBox = mapCropBox(
      {
        x: crop.x || clipX,
        y: crop.y || clipY,
        width: crop.width || clipWidth,
        height: crop.height || clipHeight,
      },
      shownImageDimensions,
      preparedCanvas.attrs,
    );
    const rotatedCropBox = getSizeAfterRotation(
      mappedCropBox.width,
      mappedCropBox.height,
      rotation,
    );
    preparedCanvas.setAttrs({
      offsetX: mappedCropBox.width / 2 + mappedCropBox.x,
      offsetY: mappedCropBox.height / 2 + mappedCropBox.y,
      width: rotatedCropBox.width,
      height: rotatedCropBox.height,
      x: rotatedCropBox.width / 2,
      y: rotatedCropBox.height / 2,
      rotation,
    });

    if (size.width) {
      const newScaleX = size.width / preparedCanvas.width();
      preparedCanvas.setAttrs({
        scaleX: newScaleX,
        width: size.width,
        x: preparedCanvas.x() * Math.abs(newScaleX),
      });
    }
    if (size.height) {
      const newScaleY = size.height / preparedCanvas.height();
      preparedCanvas.setAttrs({
        scaleY: newScaleY,
        height: size.height,
        y: preparedCanvas.y() * Math.abs(newScaleY),
      });
    }

    const finalOptions = {
      mimeType,
      pixelRatio: exportPixelRatio,
      ...(isQualityAcceptable ? { quality: quality ?? 1 } : {}),
    };
    const finalCanvas = preparedCanvas.toCanvas(finalOptions);
    const finalImgBase64 = preparedCanvas.toDataURL(finalOptions);
    const finalImgDesignState = {
      ...extractCurrentDesignState(state),
      shownImageDimensions: {
        width: state.shownImageDimensions.width,
        height: state.shownImageDimensions.height,
        scaledBy: state.shownImageDimensions.scaledBy,
      },
    };
    if (finalImgDesignState.filter) {
      finalImgDesignState.filter = finalImgDesignState.filter.name;
    }
    finalImgDesignState.finetunes = finalImgDesignState.finetunes.map(
      (finetuneFn) => finetuneFn.name,
    );
    finalImgDesignState.annotations = Object.fromEntries(
      Object.entries(finalImgDesignState.annotations).map(([k, annotation]) => {
        const imgSrc =
          annotation.name === TOOLS_IDS.IMAGE && annotation.image?.src;
        if (imgSrc && imgSrc.startsWith('blob:')) {
          return [
            k,
            {
              ...annotation,
              image: imageToBase64(annotation.image),
            },
          ];
        }
        if (annotation.image instanceof HTMLImageElement) {
          return [k, { ...annotation, image: imgSrc }];
        }
        return [k, annotation];
      }),
    );

    const finalImgPassedObject = {
      fullName: `${name}.${extension}`,
      name,
      extension,
      mimeType,
      imageCanvas: finalCanvas,
      imageBase64: finalImgBase64,
      width: size.width || mappedCropBox.width,
      height: size.height || mappedCropBox.height,
      ...(isQualityAcceptable ? { quality } : {}),
    };

    // Reseting isSaving to false so we get everything back to normal if user wants to continue editing after saving.
    designLayer.setAttr('isSaving', false);
    dispatch({ type: SET_SAVED });
    if (imgNode) {
      if (transparentBackground) {
        imgNode.show();
      }
      imgNode.clearCache();
    }

    Konva.pixelRatio = previewPixelRatio;

    const hideLoadingSpinner = () => {
      dispatch({ type: HIDE_LOADER });
    };
    if (!keepLoadingSpinnerShown) {
      hideLoadingSpinner();
    }

    return {
      imageData: finalImgPassedObject,
      designState: finalImgDesignState,
      hideLoadingSpinner,
    };
  };

  return useCloudimage ? getTransformedCloudimageData : getTransformedImgData;
};

export default useTransformedImgData;
